import { Directive, ElementRef, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { Subscription } from 'rxjs';
import { ColumnDef } from '../grid-ins.component';
import { ColumnSizes, GridResizeEventData } from './grid-resizable.directive';
import { GridResizeService } from './grid-resize.service';

@Directive({ selector: '[sklGridCellResize]' })
export class GridCellResizeDirective<TEntity, TCustomField> implements OnInit, OnDestroy {

    private minWidth!: number;
    private width!: number;
    private columnSizesChanged!: Subscription;
    private headerResizing!: Subscription;
    private viewport!: Element;

    public constructor(
        private renderer: Renderer2,
        public elementRef: ElementRef<HTMLElement>,
        private gridResize: GridResizeService,
    ) { }

    public ngOnInit(): void {
        const table = this.elementRef.nativeElement.closest('table');
        if (!table) {
            throw new Error('GridCellResizeDirective must be used inside a table');
        }
        this.viewport = table;

        this.columnSizesChanged = this.gridResize.on('ColumnSizesChanged', this.viewport, (event: ColumnSizes<TEntity>[]) => {
            const column = event.find(column => this.isDefCell(column.columnDef));

            if (column) {
                this.minWidth = column.minWidth ? parseInt(column.minWidth, 10) : NaN;
                this.setWidth(column.width ? parseInt(column.width, 10) : NaN);
            }
        });

        this.headerResizing = this.gridResize.on('HeaderResizing', this.viewport, (event: GridResizeEventData<TEntity, TCustomField>) => {
            if (this.isDefCell(event.columnDef)) {
                this.setWidth(event.width);
            }
        });
    }

    public ngOnDestroy(): void {
        this.columnSizesChanged?.unsubscribe();
        this.headerResizing?.unsubscribe();
    }

    public initCellSize(columns: ColumnSizes<TEntity>[]): void {
        const column = columns.find(column => this.isDefCell(column.columnDef));

        if (column) {
            this.minWidth = column.minWidth ? parseInt(column.minWidth, 10) : NaN;
            this.width = column.width ? parseInt(column.width, 10) : NaN;
            this.setWidth(this.width);
        }
    }

    private setWidth(width: number): void {
        if (this.minWidth && !isNaN(this.minWidth)) {
            this.renderer.setStyle(this.elementRef.nativeElement, 'min-width', `${this.minWidth}px`);
        } else {
            this.renderer.removeStyle(this.elementRef.nativeElement, 'min-width');
        }

        if (isNaN(width)) {
            this.renderer.removeStyle(this.elementRef.nativeElement, '--cell-width', 2);
            return;
        }

        if (!this.minWidth || isNaN(this.minWidth)) {
            this.renderer.setStyle(this.elementRef.nativeElement, '--cell-width', `${ width < 1 ? 1 : width }px`, 2);
        } else {
            this.renderer.setStyle(this.elementRef.nativeElement, '--cell-width', `max(${ this.minWidth }px, ${ width < 1 ? 1 : width }px)`, 2);
        }
    }

    public isDefCell<TEntity, TCustomField = never>(columnDef: ColumnDef<TEntity, TCustomField> | undefined): boolean {
        if (columnDef === undefined) {
            return false;
        }
        const columnName = (columnDef as string).replace(/\./g, '-');
        return this.elementRef.nativeElement.classList.contains(`cdk-column-${ columnName }`);
    }
}
